import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, TextField, Typography, IconButton, InputAdornment } from "@mui/material";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { onboarding, getSubscriptionInfo } from '../../services/api';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from '../../contexts/TranslationContext';
import VideoProvider from '../../components/VideoProvider';
import { videoUrls, videoTitles } from '../../utils/videoUrls';

const getCookie = (name: string): string | null => {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find((row) => row.startsWith(`${name}=`));
    return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : null;
};

let hasValidatedReferralCoupon = false;

const SubscriptionNew: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL'>('SEMIANNUAL');
    const [activeTabExpert, setActiveTabExpert] = useState<'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL'>('SEMIANNUAL');
    const [activeTabIntern, setActiveTabIntern] = useState<'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL'>('SEMIANNUAL');
    const [selectedPlan, setSelectedPlan] = useState<'seller' | 'intern'>('seller');
    const [selectedSellerCard, setSelectedSellerCard] = useState<'master' | 'expert'>('master');
    const [couponCode, setCouponCode] = useState("");
    const [verifyingCoupon, setVerifyingCoupon] = useState(false);
    const [couponStatus, setCouponStatus] = useState<'idle' | 'valid' | 'invalid'>('idle');
    const [appliedCouponCode, setAppliedCouponCode] = useState<string | null>(null);
    const [couponDiscountFactor, setCouponDiscountFactor] = useState<number | null>(null);
    const [couponSuccessMessage, setCouponSuccessMessage] = useState<string | null>(null);
    const [isCircleFilled, setIsCircleFilled] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const planPrices = {
        intern: { QUARTERLY: t('onboarding.subscription.plans_info.seller.intern.price.QUARTERLY'), SEMIANNUAL: t('onboarding.subscription.plans_info.seller.intern.price.SEMIANNUAL'), ANNUAL: t('onboarding.subscription.plans_info.seller.intern.price.ANNUAL') },
        expert: { QUARTERLY: t('onboarding.subscription.plans_info.seller.expert.price.QUARTERLY'), SEMIANNUAL: t('onboarding.subscription.plans_info.seller.expert.price.SEMIANNUAL'), ANNUAL: t('onboarding.subscription.plans_info.seller.expert.price.ANNUAL') },
        master: { QUARTERLY: t('onboarding.subscription.plans_info.seller.master.price.QUARTERLY'), SEMIANNUAL: t('onboarding.subscription.plans_info.seller.master.price.SEMIANNUAL'), ANNUAL: t('onboarding.subscription.plans_info.seller.master.price.ANNUAL') },
    }

    const convertToEnglishDigits = (value: string) =>
        value.replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))).replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)));

    const parseMillionPrice = (priceText: string) => {
        const normalized = convertToEnglishDigits(priceText);
        const numberMatch = normalized.match(/(\d+(\.\d+)?)/);
        return numberMatch ? Number(numberMatch[1]) : null;
    };

    const selectMasterDuration = (duration: 'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL') => {
        setSelectedPlan('seller');
        setSelectedSellerCard('master');
        setActiveTab(duration);
    };

    const selectExpertDuration = (duration: 'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL') => {
        setSelectedPlan('seller');
        setSelectedSellerCard('expert');
        setActiveTabExpert(duration);
    };

    const selectInternDuration = (duration: 'QUARTERLY' | 'SEMIANNUAL' | 'ANNUAL') => {
        setSelectedPlan('intern');
        setActiveTabIntern(duration);
    };

    const getPaymentPrice = () => {
        const basePriceText = selectedPlan === 'intern'
            ? planPrices.intern[activeTabIntern]
            : selectedSellerCard === 'expert'
                ? planPrices.expert[activeTabExpert]
                : planPrices.master[activeTab];

        if (couponStatus !== 'valid' || typeof couponDiscountFactor !== 'number') {
            return basePriceText;
        }

        const basePriceNumber = parseMillionPrice(basePriceText);
        if (basePriceNumber === null) {
            return basePriceText;
        }

        const discountedPrice = (basePriceNumber * (100 - couponDiscountFactor)) / 100;
        const formattedDiscountedPrice = Number(discountedPrice.toFixed(2)).toString();
        return `${formattedDiscountedPrice} میلیون تومان`;
    }

    useEffect(() => {
        // بعد دایره پایینی را پر کن
        const circleTimer = setTimeout(() => {
            setIsCircleFilled(true);
        }, 1); // بعد از پر شدن خط
        
        return () => {
            clearTimeout(circleTimer);
        };
    }, []);

    useEffect(() => {
        let interval: number;
        const checkSubscription = async () => {
            try {
                const response = await getSubscriptionInfo();
                if (response.has_active_subscription && response.data) {
                    // If subscription is active, redirect to dashboard
                    switch (response.data.plan) {
                        case 'master':
                            enqueueSnackbar(t('onboarding.subscription.messeges.success.master'), { 
                                variant: 'success',
                                autoHideDuration: 5000,
                            });
                            break;
                        case 'expert':
                            enqueueSnackbar(t('onboarding.subscription.messeges.success.expert'), { 
                                variant: 'success',
                                autoHideDuration: 5000,
                            });
                            break;
                        case 'intern':
                            enqueueSnackbar(t('onboarding.subscription.messeges.success.intern'), { 
                                variant: 'success',
                                autoHideDuration: 5000,
                            });
                            break;
                    }
                    setTimeout(() => {
                        window.location.href = '/';
                    }, 2000);
                }
            } catch (error) {
                console.error('Failed to check subscription:', error);
            }
        };

        // Start checking subscription status every 5 seconds
        interval = window.setInterval(checkSubscription, 5000);

        // Cleanup interval on component unmount
        return () => window.clearInterval(interval);
    }, [t]);

    const handleVerifyCoupon = async (codeOverride?: string) => {
        const codeToVerify = (codeOverride ?? couponCode).trim();
        if (!codeToVerify) {
            const message = t('onboarding.subscription.coupon.please_enter');
            setCouponSuccessMessage(null);
            enqueueSnackbar(message, { variant: 'warning' });
            return;
        }

        try {
            setVerifyingCoupon(true);
            setCouponSuccessMessage(null);
            const response = await onboarding.verifyCoupon(codeToVerify);
            const couponData = response?.data?.data;

            if (response?.data?.status === 'success' && couponData) {
                setAppliedCouponCode(couponData.discount_code ?? codeToVerify);
                setCouponStatus('valid');
                setCouponDiscountFactor(typeof couponData.discount_factor === 'number' ? couponData.discount_factor : null);
                const discountText =
                    typeof couponData.discount_factor === 'number'
                        ? `${t('onboarding.subscription.coupon.discount_factor')} ${couponData.discount_factor}% ${t('onboarding.subscription.coupon.discount_factor_helper')}`
                        : t('onboarding.subscription.coupon.valid_coupon');
                setCouponSuccessMessage(discountText);
                enqueueSnackbar(response?.data?.message ?? t('onboarding.subscription.coupon.valid_coupon'), { variant: 'success', autoHideDuration: 2000  });
            } else {
                setAppliedCouponCode(null);
                setCouponStatus('invalid');
                setCouponDiscountFactor(null);
                const detailMessage = response?.data?.detail || response?.data?.message || t('onboarding.subscription.coupon.invalid_coupon');
                enqueueSnackbar(detailMessage, { variant: 'error', autoHideDuration: 2000  });
            }
        } catch (error: any) {
            const backendMessage = error?.response?.data?.detail || error?.response?.data?.message;
            setAppliedCouponCode(null);
            setCouponStatus('invalid');
            setCouponDiscountFactor(null);
            const fallbackMessage = backendMessage ?? t('onboarding.subscription.coupon.error_checking_coupon');
            enqueueSnackbar(fallbackMessage, { variant: 'error', autoHideDuration: 2000  });
        } finally {
            setVerifyingCoupon(false);
        }
    };

    useEffect(() => {
        if (hasValidatedReferralCoupon) return;

        const referralCoupon = getCookie('r')?.trim();
        if (!referralCoupon) return;

        hasValidatedReferralCoupon = true;
        setCouponCode(referralCoupon);
        void handleVerifyCoupon(referralCoupon);
    }, []);

    const handlePayment = async () => {
        if (isProcessingPayment) {
            return;
        }

        // Safari iOS blocks window.open after await — open blank tab synchronously on click
        const paymentWindow = window.open('about:blank', '_blank');

        try {
            setIsProcessingPayment(true);
            enqueueSnackbar(t('onboarding.subscription.payment.processing'), { variant: 'info', autoHideDuration: 2000 });
            
            // تعیین plan: intern یا expert یا master
            const plan: 'master' | 'expert' | 'intern' = selectedPlan === 'intern'
                ? 'intern'
                : selectedSellerCard === 'master'
                    ? 'master'
                    : 'expert';

            // duration: QUARTERLY یا SEMIANNUAL یا ANNUAL
            const duration = selectedPlan === 'intern'
                ? activeTabIntern
                : selectedSellerCard === 'master'
                    ? activeTab
                    : activeTabExpert;

            // discount_code از appliedCouponCode یا null
            const discount_code = appliedCouponCode && appliedCouponCode.trim() ? appliedCouponCode.trim() : null;

            const response = await onboarding.initialSeller({
                plan,
                duration,
                discount_code,
            });

            const paymentUrl = response.data?.data?.payment_url;
            if (!paymentUrl) {
                paymentWindow?.close();
                return;
            }

            if (paymentWindow && !paymentWindow.closed) {
                paymentWindow.location.href = paymentUrl;
            } else {
                // Fallback when popup is blocked (common on iPhone Safari)
                window.location.href = paymentUrl;
            }
        } catch (error) {
            paymentWindow?.close();
            enqueueSnackbar(t('onboarding.subscription.messeges.error'), { variant: 'error', autoHideDuration: 3000 });
        } finally {
            setIsProcessingPayment(false);
        }
    };

    return (
        <VideoProvider videoUrl={videoUrls.signup} videoTitle={videoTitles.signup}>
        <Box sx={{ width: '100%', height: '100%', padding: {xs:'20px 0px', sm:'20px 40px'}, display: 'flex', justifyContent: 'center', alignItems: 'center' , background: 'linear-gradient(152deg,rgba(103, 240, 208, 1) 18%, rgba(255, 255, 255, 1) 100%)'}}>
            <Box sx={{ width: '100%', maxWidth: 1100, display: 'flex', flexDirection: 'column', alignItems: 'center', gap:'40px', padding: '0px' }}>
                <Box component="img" src="/images/aida-logo-ok.svg" alt="" sx={{ width: { xs: 85, sm: 100 } }} />
                <Box sx={{ width: '100%', display: 'flex', position: 'relative', gap: '20px' }}>
                    {/* Stepper */}
                    <Box sx={{ 
                        display: { md: 'none', lg: 'flex' }, 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        position: 'absolute',
                        right: '-60px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        '@media (max-width: 900px)': {
                            display: 'none'
                        }
                    }}>
                        {/* دایره بالایی - مرحله 1 */}
                        <Box sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#00AF9E',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '18px',
                            fontWeight: 600,
                            border: '2px solid #fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            lineHeight: 1,
                            textAlign: 'center'
                        }}>
                            ۱
                        </Box>
                        {/* خط اتصال */}
                        <Box sx={{
                            width: '3px',
                            height: '60px',
                            backgroundColor: '#00AF9E',
                            margin: '0'
                        }} />
                        {/* دایره پایینی - مرحله 2 */}
                        <Box sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: isCircleFilled ? '#00AF9E' : '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isCircleFilled ? '#fff' : '#000',
                            fontSize: '18px',
                            fontWeight: 600,
                            border: '2px solid #fff',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            lineHeight: 1,
                            textAlign: 'center',
                            transition: 'background-color 0.8s ease, color 0.8s ease',
                        }}>
                            ۲
                        </Box>
                    </Box>

                    
                    <Box sx={{ width: '100%',display: 'flex', flexDirection: 'column', alignItems: 'center', gap:'40px', padding: '30px 20px' , backgroundColor: '#fff', borderRadius: '20px', position: 'relative' }}>
                        {/* دکمه برگشت */}
                        <IconButton
                            onClick={() => navigate('/onboarding')}
                            sx={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                backgroundColor: '#f5f5f5',
                                color: '#000',
                                '&:hover': {
                                    backgroundColor: '#e0e0e0',
                                },
                                width: '40px',
                                height: '40px',
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>

                        <Typography variant="h6" sx={{fontSize: {xs: '22px', sm: '24px', md: '26px'}, fontWeight: 600, color: '#000'}}>اشتراک های آیدا</Typography>

                        <Box 
                        sx={{
                            width: '100%', 
                            display: 'flex', 
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'center', md: 'flex-start' }, 
                            justifyContent: 'center',
                            gap:'20px', 
                            flexWrap: 'wrap'
                        }}
                        >
                            {/* master Plan */}
                            <Box 
                            sx={{
                                width: { xs: '100%' , md: 350 }, 
                                maxWidth: '350px', 
                                minWidth: '250px',
                                padding: '3px',
                                borderRadius: '10px',
                                background: 'linear-gradient(90deg, #ff0000 100%, #ff9a00 10%, #d0de21 20%, #4fdc4a 30%, #3fdad8 40%, #2fc9e2 50%, #1c7fee 60%, #5f15f2 70%, #ba0cf8 80%, #fb07d9 90%, #ff0000 100%)',
                                backgroundSize: '200% 100%',
                                animation: 'anim 3s linear infinite',
                                transition: 'all 0.3s ease',
                                "&:hover": {
                                    boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
                                    transform: 'translateY(-4px) scale(1.05)',
                                },
                                "@keyframes anim": {
                                    "0%": {
                                        backgroundPosition: "0% 50%",
                                    },
                                    "100%": {
                                        backgroundPosition: "200% 50%",
                                    }
                                }
                            }}
                            >
                                <Box 
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    gap: { xs: '12px', sm: '16px', md: '20px' }, 
                                    padding: { xs: '12px', sm: '16px', md: '20px' }, 
                                    borderRadius: '7px', 
                                    // border: '1px solid #000', 
                                    backgroundColor: '#fff',
                                }}
                            >
                                <img src="/images/main.png" alt="" style={{width: 'clamp(60px, 8vw, 85px)'}} />
                                <p style={{fontSize: 'clamp(16px, 2.5vw, 20px)', fontWeight: 800, margin: '0px'}}>{t('onboarding.subscription.plans_info.seller.master.title')}</p>
                                <Box sx={{display: 'flex' , alignItems: 'center'}}>
                                    <Button 
                                        fullWidth 
                                        onClick={() => selectMasterDuration('QUARTERLY')}
                                        sx={{
                                            width:'max-content',
                                            padding: { xs: '4px 6px', sm: '4px 7px', md: '5px 8px' },
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            borderRadius: '20px 0px 0px 20px', 
                                            border:'1px solid #000', 
                                            whiteSpace: 'nowrap',
                                            color: (selectedPlan === 'seller' && selectedSellerCard === 'master' && activeTab === 'QUARTERLY') ? '#fff' : '#000', 
                                            backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'master' && activeTab === 'QUARTERLY') ? '#009688' : '#D9D9D9', 
                                            "&:hover": {
                                                backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'master' && activeTab === 'QUARTERLY') ? '#009688' : '#D9D9D9'
                                            }
                                        }}
                                    >
                                        {t('onboarding.subscription.durations.QUARTERLY')}
                                    </Button>
                                    <Button 
                                        fullWidth 
                                        onClick={() => selectMasterDuration('SEMIANNUAL')}
                                        sx={{
                                            width:'max-content',
                                            padding: { xs: '4px 10px', sm: '4px 12px', md: '5px 15px' },
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            borderRadius: '0px 0px 0px 0px', 
                                            border:'1px solid #000', 
                                            borderLeft: 'none', 
                                            color: (selectedPlan === 'seller' && selectedSellerCard === 'master' && activeTab === 'SEMIANNUAL') ? '#fff' : '#000', 
                                            backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'master' && activeTab === 'SEMIANNUAL') ? '#009688' : '#D9D9D9',
                                            whiteSpace: 'nowrap',
                                            "&:hover": {
                                                backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'master' && activeTab === 'SEMIANNUAL') ? '#009688' : '#D9D9D9'
                                            }
                                        }}
                                    >
                                        {t('onboarding.subscription.durations.SEMIANNUAL')}
                                    </Button>
                                    <Button 
                                        fullWidth 
                                        onClick={() => selectMasterDuration('ANNUAL')}
                                        sx={{
                                            width:'max-content',
                                            padding: { xs: '4px 6px', sm: '4px 7px', md: '5px 8px' },
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            borderRadius: '0px 20px 20px 0px', 
                                            border:'1px solid #000', 
                                            borderLeft: 'none', 
                                            whiteSpace: 'nowrap',
                                            color: (selectedPlan === 'seller' && selectedSellerCard === 'master' && activeTab === 'ANNUAL') ? '#fff' : '#000', 
                                            backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'master' && activeTab === 'ANNUAL') ? '#009688' : '#D9D9D9', 
                                            "&:hover": {
                                                backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'master' && activeTab === 'ANNUAL') ? '#009688' : '#D9D9D9'
                                            }
                                        }}
                                    >
                                        {t('onboarding.subscription.durations.ANNUAL')}
                                    </Button>
                                </Box>
                                <Box 
                                    component="ul" 
                                    sx={{
                                        listStyle: 'none',
                                        padding: 0,
                                        margin: { xs: '12px 0 0 0', sm: '16px 0 0 0', md: '20px 0 0 0' },
                                        width: '100%',
                                        maxWidth: { xs: 220, sm: 240, md: 270 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: { xs: '8px', sm: '9px', md: '10px' },
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.master.features.0')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.master.features.1')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.master.features.2')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.master.features.3')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.master.features.4')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.master.features.5')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.master.features.6')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.master.features.7')}</span>
                                    </Box>
                                </Box>
                                <Button 
                                    variant="contained" 
                                    fullWidth
                                    onClick={() => { setSelectedPlan('seller'); setSelectedSellerCard('master'); }}
                                    sx={{
                                      backgroundColor: selectedPlan === 'seller' && selectedSellerCard === 'master' ? '#00AF9E' : '#D9D9D9',
                                      color: selectedPlan === 'seller' && selectedSellerCard === 'master' ? '#fff' : '#000',
                                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                                      fontWeight: 600,
                                      borderRadius: '10px',
                                      height: { xs: '38px', sm: '42px', md: '45px' },
                                      boxShadow:'none',
                                      "&:hover": {
                                        backgroundColor: selectedPlan === 'seller' && selectedSellerCard === 'master' ? '#00AF9E' : '#D9D9D9'
                                      },
                                      "&:disabled": {
                                        backgroundColor: '#cccccc',
                                        color: '#666666'
                                      }
                                    }}
                                >{t('onboarding.subscription.select')}</Button>
                                </Box>
                            </Box>

                            {/* expert Plan */}
                            <Box 
                            sx={{
                                width: { xs: '100%' , md: 350 }, 
                                maxWidth: '350px', 
                                minWidth: '250px',
                                padding: '3px',
                                borderRadius: '10px',
                                border: '1px solid #000',
                                transition: 'all 0.3s ease',
                                "&:hover": {
                                    boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
                                    transform: 'translateY(-4px) scale(1.05)',
                                },
                            }}
                            >
                                <Box 
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    gap: { xs: '12px', sm: '16px', md: '20px' }, 
                                    padding: { xs: '12px', sm: '16px', md: '20px' }, 
                                    borderRadius: '7px', 
                                    // border: '1px solid #000', 
                                    backgroundColor: '#fff',
                                }}
                            >
                                <img src="/images/expert.png" alt="" style={{width: 'clamp(60px, 8vw, 85px)'}} />
                                <p style={{fontSize: 'clamp(16px, 2.5vw, 20px)', fontWeight: 800, margin: '0px'}}>{t('onboarding.subscription.plans_info.seller.expert.title')}</p>
                                <Box sx={{display: 'flex' , alignItems: 'center'}}>
                                    <Button 
                                        fullWidth 
                                        onClick={() => selectExpertDuration('QUARTERLY')}
                                        sx={{
                                            width:'max-content',
                                            padding: { xs: '4px 6px', sm: '4px 7px', md: '5px 8px' },
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            borderRadius: '20px 0px 0px 20px', 
                                            border:'1px solid #000', 
                                            whiteSpace: 'nowrap',
                                            color: (selectedPlan === 'seller' && selectedSellerCard === 'expert' && activeTabExpert === 'QUARTERLY') ? '#fff' : '#000', 
                                            backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'expert' && activeTabExpert === 'QUARTERLY') ? '#009688' : '#D9D9D9', 
                                            "&:hover": {
                                                backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'expert' && activeTabExpert === 'QUARTERLY') ? '#009688' : '#D9D9D9'
                                            }
                                        }}
                                    >
                                        {t('onboarding.subscription.durations.QUARTERLY')}
                                    </Button>
                                    <Button 
                                        fullWidth 
                                        onClick={() => selectExpertDuration('SEMIANNUAL')}
                                        sx={{
                                            width:'max-content',
                                            padding: { xs: '4px 10px', sm: '4px 12px', md: '5px 15px' },
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            borderRadius: '0px 0px 0px 0px', 
                                            border:'1px solid #000', 
                                            borderLeft: 'none', 
                                            color: (selectedPlan === 'seller' && selectedSellerCard === 'expert' && activeTabExpert === 'SEMIANNUAL') ? '#fff' : '#000', 
                                            backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'expert' && activeTabExpert === 'SEMIANNUAL') ? '#009688' : '#D9D9D9',
                                            whiteSpace: 'nowrap',
                                            "&:hover": {
                                                backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'expert' && activeTabExpert === 'SEMIANNUAL') ? '#009688' : '#D9D9D9'
                                            }
                                        }}
                                    >
                                        {t('onboarding.subscription.durations.SEMIANNUAL')}
                                    </Button>
                                    <Button 
                                        fullWidth 
                                        onClick={() => selectExpertDuration('ANNUAL')}
                                        sx={{
                                            width:'max-content',
                                            padding: { xs: '4px 6px', sm: '4px 7px', md: '5px 8px' },
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            borderRadius: '0px 20px 20px 0px', 
                                            border:'1px solid #000', 
                                            borderLeft: 'none', 
                                            whiteSpace: 'nowrap',
                                            color: (selectedPlan === 'seller' && selectedSellerCard === 'expert' && activeTabExpert === 'ANNUAL') ? '#fff' : '#000', 
                                            backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'expert' && activeTabExpert === 'ANNUAL') ? '#009688' : '#D9D9D9', 
                                            "&:hover": {
                                                backgroundColor: (selectedPlan === 'seller' && selectedSellerCard === 'expert' && activeTabExpert === 'ANNUAL') ? '#009688' : '#D9D9D9'
                                            }
                                        }}
                                    >
                                        {t('onboarding.subscription.durations.ANNUAL')}
                                    </Button>
                                </Box>
                                <Box 
                                    component="ul" 
                                    sx={{
                                        listStyle: 'none',
                                        padding: 0,
                                        margin: { xs: '12px 0 0 0', sm: '16px 0 0 0', md: '20px 0 0 0' },
                                        width: '100%',
                                        maxWidth: { xs: 220, sm: 240, md: 270 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: { xs: '8px', sm: '9px', md: '10px' },
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.expert.features.0')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.expert.features.1')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.expert.features.2')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.expert.features.3')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.expert.features.4')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.expert.features.5')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.expert.features.6')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.expert.features.7')}</span>
                                    </Box>
                                </Box>
                                <Button 
                                    variant="contained" 
                                    fullWidth
                                    onClick={() => { setSelectedPlan('seller'); setSelectedSellerCard('expert'); }}
                                    sx={{
                                      backgroundColor: selectedPlan === 'seller' && selectedSellerCard === 'expert' ? '#00AF9E' : '#D9D9D9',
                                      color: selectedPlan === 'seller' && selectedSellerCard === 'expert' ? '#fff' : '#000',
                                      fontSize: { xs: '16px', sm: '18px', md: '20px' },
                                      fontWeight: 600,
                                      borderRadius: '10px',
                                      height: { xs: '38px', sm: '42px', md: '45px' },
                                      boxShadow:'none',
                                      "&:hover": {
                                        backgroundColor: selectedPlan === 'seller' && selectedSellerCard === 'expert' ? '#00AF9E' : '#D9D9D9'
                                      },
                                      "&:disabled": {
                                        backgroundColor: '#cccccc',
                                        color: '#666666'
                                      }
                                    }}
                                >{t('onboarding.subscription.select')}</Button>
                                </Box>
                            </Box>

                            {/* intern Plan */}
                            <Box sx={{
                                width: { xs: '100%', md: 350 }, 
                                minWidth: '250px',
                                maxWidth: '350px', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                gap: { xs: '12px', sm: '16px', md: '20px' }, 
                                padding: { xs: '12px', sm: '16px', md: '20px' }, 
                                borderRadius: '10px', 
                                border: '1px solid #000', 
                                backgroundColor: '#fff',
                                transition: 'all 0.3s ease',
                                "&:hover": {
                                    backgroundColor: '#f5f5f5',
                                    boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
                                    transform: 'translateY(-4px) scale(1.05)',
                                }
                            }}>
                                <img src="/images/sales-expert.png" alt="" style={{width: 'clamp(60px, 8vw, 85px)'}} />
                                <p style={{fontSize: 'clamp(16px, 2.5vw, 20px)', fontWeight: 800, margin: '0px'}}>{t('onboarding.subscription.plans_info.seller.intern.title')}</p>
                                <Box sx={{display: 'flex' , alignItems: 'center'}}>
                                    <Button 
                                        fullWidth 
                                        onClick={() => selectInternDuration('QUARTERLY')}
                                        sx={{
                                            width:'max-content',
                                            padding: { xs: '4px 6px', sm: '4px 7px', md: '5px 8px' },
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            borderRadius: '20px 0px 0px 20px', 
                                            border:'1px solid #000', 
                                            whiteSpace: 'nowrap',
                                            color: (selectedPlan === 'intern' && activeTabIntern === 'QUARTERLY') ? '#fff' : '#000', 
                                            backgroundColor: (selectedPlan === 'intern' && activeTabIntern === 'QUARTERLY') ? '#009688' : '#D9D9D9', 
                                            "&:hover": {
                                                backgroundColor: (selectedPlan === 'intern' && activeTabIntern === 'QUARTERLY') ? '#009688' : '#D9D9D9'
                                            }
                                        }}
                                    >
                                        {t('onboarding.subscription.durations.QUARTERLY')}
                                    </Button>
                                    <Button 
                                        fullWidth 
                                        onClick={() => selectInternDuration('SEMIANNUAL')}
                                        sx={{
                                            width:'max-content',
                                            padding: { xs: '4px 10px', sm: '4px 12px', md: '5px 15px' },
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            borderRadius: '0px 0px 0px 0px', 
                                            border:'1px solid #000', 
                                            borderLeft: 'none', 
                                            color: (selectedPlan === 'intern' && activeTabIntern === 'SEMIANNUAL') ? '#fff' : '#000', 
                                            backgroundColor: (selectedPlan === 'intern' && activeTabIntern === 'SEMIANNUAL') ? '#009688' : '#D9D9D9',
                                            whiteSpace: 'nowrap',
                                            "&:hover": {
                                                backgroundColor: (selectedPlan === 'intern' && activeTabIntern === 'SEMIANNUAL') ? '#009688' : '#D9D9D9'
                                            }
                                        }}
                                    >
                                        {t('onboarding.subscription.durations.SEMIANNUAL')}
                                    </Button>
                                    <Button 
                                        fullWidth 
                                        onClick={() => selectInternDuration('ANNUAL')}
                                        sx={{
                                            width:'max-content',
                                            padding: { xs: '4px 6px', sm: '4px 7px', md: '5px 8px' },
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            borderRadius: '0px 20px 20px 0px', 
                                            border:'1px solid #000', 
                                            borderLeft: 'none', 
                                            whiteSpace: 'nowrap',
                                            color: (selectedPlan === 'intern' && activeTabIntern === 'ANNUAL') ? '#fff' : '#000', 
                                            backgroundColor: (selectedPlan === 'intern' && activeTabIntern === 'ANNUAL') ? '#009688' : '#D9D9D9', 
                                            "&:hover": {
                                                backgroundColor: (selectedPlan === 'intern' && activeTabIntern === 'ANNUAL') ? '#009688' : '#D9D9D9'
                                            }
                                        }}
                                    >
                                        {t('onboarding.subscription.durations.ANNUAL')}
                                    </Button>
                                </Box>
                                <Box 
                                    component="ul" 
                                    sx={{
                                        listStyle: 'none',
                                        padding: 0,
                                        margin: '0px',
                                        width: '100%',
                                        maxWidth: { xs: 220, sm: 240, md: 270 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: { xs: '8px', sm: '9px', md: '10px' },
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.intern.features.0')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.intern.features.1')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.intern.features.2')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.intern.features.3')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.intern.features.4')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.intern.features.5')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.intern.features.6')}</span>
                                    </Box>
                                    <Box 
                                        component="li" 
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: { xs: '6px', sm: '7px', md: '8px' },
                                            width: '100%',
                                            fontSize: { xs: '12px', sm: '13px', md: '14px' },
                                            color: '#000'
                                        }}
                                    >
                                        <img src="/images/OK.svg" alt="✓" style={{ width: 'clamp(16px, 2.5vw, 20px)', height: 'clamp(16px, 2.5vw, 20px)', flexShrink: 0 }} />
                                        <span>{t('onboarding.subscription.plans_info.seller.intern.features.7')}</span>
                                    </Box>
                                </Box>
                                <Button 
                                    variant="contained" 
                                    fullWidth
                                    onClick={() => setSelectedPlan('intern')}
                                    sx={{
                                        backgroundColor: selectedPlan === 'intern' ? '#00AF9E' : '#D9D9D9',
                                        color: selectedPlan === 'intern' ? '#fff' : '#000',
                                        fontSize: { xs: '16px', sm: '18px', md: '20px' },
                                        fontWeight: 600,
                                        borderRadius: '10px',
                                        height: { xs: '38px', sm: '42px', md: '45px' },
                                        boxShadow:'none',
                                        "&:hover": {
                                            backgroundColor: selectedPlan === 'intern' ? '#00AF9E' : '#D9D9D9'
                                        }
                                    }}
                                >{t('onboarding.subscription.select')}</Button>
                            </Box>
                        </Box>
    
                        <Box sx={{width: '100%', maxWidth: 450, display: 'flex' , flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap:'20px'}}>
                        <TextField
                            label={t('onboarding.subscription.coupon.coupon_code')}
                            placeholder="ABCDEFG"
                            fullWidth
                            value={couponCode}
                            onChange={(e) => {
                                setCouponCode(e.target.value);
                                setCouponStatus('idle');
                                setAppliedCouponCode(null);
                                setCouponDiscountFactor(null);
                                setCouponSuccessMessage(null);
                            }}
                            InputLabelProps={{
                                sx: {
                                    fontSize: 16,
                                    color: '#000',
                                    textAlign: 'right',
                                }
                            }}
                            InputProps={{
                                sx: {
                                    height: '55px',
                                    paddingRight: '7px',
                                    '& .MuiInputAdornment-root': {
                                        height: 'auto',
                                        maxHeight: 'none',
                                        alignSelf: 'center',
                                        flexShrink: 0,
                                    },
                                },
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        sx={{
                                            ml: 0,
                                            mr: 0,
                                            height: 'auto',
                                            maxHeight: 'none',
                                            width: couponStatus === 'idle'
                                                ? { xs: '64px', md: 'fit-content' }
                                                : { xs: '36px', md: 'fit-content' },
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Button
                                            aria-label="verify coupon"
                                            onClick={() => handleVerifyCoupon()}
                                            disabled={verifyingCoupon}
                                            sx={{
                                                backgroundColor: couponStatus === 'valid'
                                                    ? '#4CAF50'
                                                    : couponStatus === 'invalid'
                                                    ? '#D32F2F'
                                                    : '#2196F3',
                                                color: '#fff',
                                                borderRadius: 1,
                                                flexShrink: 0,
                                                boxSizing: 'border-box',
                                                width: couponStatus === 'idle' ? 64 : { xs: 36, sm: 36 },
                                                minWidth: couponStatus === 'idle' ? 64 : { xs: 36, sm: 36 },
                                                height: 36,
                                                minHeight: 36,
                                                p: couponStatus === 'idle' ? undefined : 0,
                                                px: couponStatus === 'idle' ? 1.5 : 0,
                                                textTransform: 'none',
                                                fontSize: 14,
                                                fontWeight: 700,
                                                '&:hover': {
                                                    backgroundColor: couponStatus === 'valid'
                                                        ? '#388E3C'
                                                        : couponStatus === 'invalid'
                                                        ? '#C62828'
                                                        : '#1976D2'
                                                },
                                                '& svg': { fontSize: { xs: 24, sm: 22 } },
                                            }}
                                        >
                                            {couponStatus === 'valid' ? (
                                                <CheckIcon />
                                            ) : couponStatus === 'invalid' ? (
                                                <CloseIcon />
                                            ) : (
                                                'اعمال'
                                            )}
                                        </Button>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    overflow: 'hidden',
                                    height: '55px',
                                    borderRadius: '10px',
                                    backgroundColor: '#ffffff',
                                    '& .MuiInputBase-input': {
                                        fontSize: 16,
                                        color: '#000',
                                        padding: '0 15px',
                                        backgroundColor: '#fff',
                                        height: '100%',
                                    },
                                }
                            }}
                        />
                        {couponSuccessMessage && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'success.main',
                                    mt: '-10px',
                                    fontSize: '14px',
                                }}
                            >
                                {couponSuccessMessage}
                            </Typography>
                        )}
                        <Button 
                            variant="contained" 
                            fullWidth
                            onClick={handlePayment}
                            disabled={isProcessingPayment}
                            sx={{
                                backgroundColor:'#00AF9E',
                                color:'#fff',
                                fontSize: 20,
                                fontWeight: 600,
                                borderRadius: '10px',
                                height: '55px',
                                boxShadow:'none',
                                "&:hover": {
                                    backgroundColor:'#00AF9E'
                                },
                                "&:disabled": {
                                    backgroundColor: '#cccccc',
                                    color: '#666666'
                                }
                            }}
                        >{isProcessingPayment ? 'در حال پردازش...' : `پرداخت ${getPaymentPrice()}`}</Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
        </VideoProvider>
    );
};

export default SubscriptionNew;
